import React, { useState, useEffect } from "react";
import styled from "styled-components";
import CommentModal from "./commentModal";
import ReportModal from "./ReportModal";
import { StyledText } from "../../../../components/common/basic";
import { MenuIcon, HeartIcon, CommentIcon } from "../../../../components/svgs";
import { formatDate } from "../utils/dateUtils";
import usePeerTalk from "../hooks/usePeerTalk";
import PinnedIcon from "../../../../components/svgs/pinnedIcon";
import { formatDistanceToNow } from "date-fns";

const CommentCard = ({
  comments: initialComments = [],
  storyId,
  storyColor,
}) => {
  const [comments, setComments] = useState(initialComments);
  const [showCommentModal, setShowCommentModal] = useState({});
  const [showReportModal, setShowReportModal] = useState(false);
  const { userDetails, handleLike, handleDelete, handlePin, handleReport } =
    usePeerTalk();

  useEffect(() => {
    setComments(initialComments);
  }, [initialComments]);

  const toggleCommentModal = (commentId) => {
    setShowCommentModal((prevState) => ({
      ...prevState,
      [commentId]: !prevState[commentId],
    }));
  };

  const handleReportModalClose = () => {
    setShowReportModal(false);
  };

  const handleLikeSubmit = async (commentId) => {
    try {
      await handleLike(storyId, commentId);

      setComments((prevComments) =>
        prevComments.map((comment) =>
          comment._id === commentId
            ? {
                ...comment,
                likes: comment.likes.includes(userDetails.msisdn)
                  ? comment.likes.filter((id) => id !== userDetails.msisdn)
                  : [...comment.likes, userDetails.msisdn],
              }
            : comment
        )
      );
    } catch (error) {
      console.error("Error liking comment:", error);
    }
  };

  const handleDeleteSubmit = async (commentId) => {
    try {
      await handleDelete(commentId, storyId);

      setComments((prevComments) =>
        prevComments.filter((comment) => comment._id !== commentId)
      );
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const handlePinSubmit = async (commentId) => {
    try {
      await handlePin(commentId, storyId);

      setComments((prevComments) => {
        const updatedComments = prevComments.map((comment) =>
          comment._id === commentId
            ? { ...comment, pinned: !comment.pinned }
            : comment
        );

        updatedComments.sort((a, b) => b.pinned - a.pinned);

        return updatedComments;
      });
    } catch (error) {
      console.error("Error pinning comment:", error);
    }
  };

  const handleReportSubmit = async (commentId) => {
    try {
      await handleReport(commentId, storyId, userDetails.msisdn);
      setComments((prevComments) =>
        prevComments.map((comment) =>
          comment._id === commentId
            ? {
                ...comment,
                reported: comment.reported.includes(userDetails.msisdn)
                  ? comment.reported.filter((id) => id !== userDetails.msisdn)
                  : [...comment.reported, userDetails.msisdn],
              }
            : comment
        )
      );
    } catch (error) {
      console.error("Error reporting comment:", error);
    }
  };

  const userId = userDetails ? userDetails.msisdn : null;

  return (
    <>
      {comments.length === 0 ? (
        <>
          <NoCommentsHeader>Comment(s)</NoCommentsHeader>
          <IconsContainer>
            <CommentIcon fill="#CBCBCB" width="36" height="36" />
          </IconsContainer>
          <NoCommentsMessage>Be the first to comment</NoCommentsMessage>
        </>
      ) : (
        <>
          <StyledText size={14} style={{ fontFamily: "MTNRegular" }}>
            Comment(s)
          </StyledText>
          {comments
            .sort((a, b) => b.pinned - a.pinned)
            .map((comment, index) => {
              const isLiked = userId && comment.likes.includes(userId);
              const isPinned = comment.pinned || false;
              const isMyComment = comment.userId === userId;
              const isReported = userId && comment.reported.includes(userId);

              return (
                <CommentWrapper key={comment._id || `comment-${index}`}>
                  <TopItems>
                    <TopLeftItems>
                      <CommentNumber backgroundColor={storyColor}>
                        {isPinned ? <PinnedIcon /> : <span>{index + 1}</span>}
                      </CommentNumber>
                      <div
                        style={{
                          color: "#777",
                          fontSize: "20px",
                          margin: "0 4px 0 4px",
                          fontWeight: "bold",
                        }}
                      >
                        ·
                      </div>
                      <StyledText
                        size={12}
                        style={{
                          margin: 0,
                          marginTop: 2,
                          fontFamily: "MTNRegular",
                        }}
                      >
                        {formatDistanceToNow(new Date(comment.date))}
                      </StyledText>
                    </TopLeftItems>
                    <Button onClick={() => toggleCommentModal(comment._id)}>
                      <MenuIcon />
                    </Button>
                  </TopItems>
                  <StyledText
                    size={13}
                    style={{
                      fontFamily: "MTNRegular",
                      margin: 0,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {comment.content}
                  </StyledText>
                  <BottomLeftItems>
                    <Button
                      onClick={() => handleLikeSubmit(comment._id)}
                      style={{ cursor: "pointer" }}
                    >
                      <HeartIcon isLiked={isLiked} />
                    </Button>
                    <StyledText
                      size={12}
                      style={{
                        marginLeft: 5,
                        fontFamily: "MTNRegular",
                        color: "#777",
                      }}
                    >
                      {comment.likes.length}
                    </StyledText>
                  </BottomLeftItems>
                  {showCommentModal[comment._id] && (
                    <CommentModal
                      show={showCommentModal[comment._id]}
                      onClose={() => toggleCommentModal(comment._id)}
                      onReportClick={() => handleReportSubmit(comment._id)}
                      commentId={comment._id}
                      onDeleteClick={() => handleDeleteSubmit(comment._id)}
                      onPinClick={() => handlePinSubmit(comment._id)}
                      isPinned={isPinned}
                      isMyComment={isMyComment}
                      isReported={isReported}
                    />
                  )}
                  {showReportModal && (
                    <ReportModal
                      onReport={handleReportModalClose}
                      onCancel={handleReportModalClose}
                      title="Report Comment?"
                      description="This comment will be forwarded to the Youngster Pulse team. The commenter will not be notified."
                      action="Report"
                    />
                  )}
                </CommentWrapper>
              );
            })}
        </>
      )}
    </>
  );
};

export default CommentCard;

const CommentWrapper = styled.div`
  margin-bottom: 12px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 7px;
`;

const TopLeftItems = styled.div`
  display: flex;
  width: 60%;
  flex-direction: row;
  align-items: center;
  margin-bottom: 7px;
`;

const BottomLeftItems = styled.div`
  display: flex;
  width: 30%;
  flex-direction: row;
  align-items: center;
`;

const TopItems = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const Button = styled.button`
  border: none;
  background: white;
  padding: 0;
`;

const CommentNumber = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  background-color: ${(props) => props.backgroundColor};
`;

const NoCommentsMessage = styled.p`
  font-family: "MTNRegular";
  font-size: 14px;
  color: #cbcbcb;
  text-align: center;
  margin-top: 10px;
`;

const NoCommentsHeader = styled.h1`
  font-family: "MTNRegular";
  font-size: 16px;
  color: #333;
  text-align: left;
  margin-top: 20px;
`;

const IconsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;
